/**
 * Point d'entrée de la simulation : un tick fixe fait avancer le joueur, l'IA, les projectiles,
 * les ressources et la mission. Aucun accès au rendu ni au navigateur.
 *
 * Monde persistant : le joueur part amarré à un dock (station ou porteur), choisit une mission à quai,
 * décolle ; la mission commence au décollage et se termine sans arrêter la simulation.
 * Une simulation se déroule dans une zone (zones.json) : son arène, ses docks, sa forteresse éventuelle.
 * Changer de zone (saut du porteur) crée une nouvelle simulation.
 */
import { jumpTargets, zoneData, zoneOf, type DockId, type GameData, type SectorId, type ShipId } from '../data';
import { Quat, Vec3 } from './core/math';
import { Ship, emptyCommand, type PlayerControl } from './entities';
import { Dock, dockingPose, launchPose } from './encounter/dock';
import { Fortress } from './encounter/fortress';
import { Mission, type MissionMode, type SpawnRequest } from './encounter/mission';
import { updatePickups } from './encounter/mission';
import { onScoreKill, resetStreak, updateScore } from './encounter/score';
import { applyRotation, applyThrust, playerMotorProfile } from './flight/motor';
import { BossBrain, createBoss } from './pilots/ai/boss';
import { createEnemy, type EnemyBrain } from './pilots/ai/enemy';
import { createTurret } from './pilots/ai/turret';
import { PlayerPilot, type MouseMode, type PlayerInput } from './pilots/player';
import { SimState } from './state';
import { resetLock, updateLock } from './weapons/lock';
import { createPlayerWeapons, updatePlayerWeapons } from './weapons/player';
import { updateProjectiles } from './weapons/projectiles';
import { CollisionWorld, initPhysics } from './world/collision';
import { stationBoxes } from './world/station';
import { groundField } from './world/ground';
import { applyLoadout, type Loadout } from './loadout';

export const TICK_RATE = 120;
export const TICK_DT = 1 / TICK_RATE;

export interface SimOptions {
  shipId: ShipId;
  mode: MissionMode;
  seed: number;
  mouseMode: MouseMode;
  /** Conserve les entrées de chaque tick pour le rejeu. */
  recordInputs?: boolean;
  /** Départ à quai (défaut). Faux : vol libre à l'origine (tests). */
  startDocked?: boolean;
  /** Dock de départ (défaut : porteur). */
  startDock?: DockId;
  /**
   * Catapulte automatique au départ et après une réapparition (défaut : vrai, pour les tests et le pilote
   * automatique). Le jeu la désactive : on décolle depuis le menu du dock.
   */
  autoLaunch?: boolean;
  /** Équipement (phase C). Absent : châssis nu de ships.json, sans bouclier. */
  loadout?: Partial<Loadout>;
  /** Zone (zones.json) ; défaut : zone de départ. */
  zone?: string;
  /** Tick de départ : le temps continue d'une zone à l'autre (télémétrie de la partie). */
  startTick?: number;
  /** Départ en vol à cette pose et cette vitesse (arrivée d'un saut de chasseur) ; prime sur `startDocked`. */
  startPose?: { position: readonly number[]; rotation: readonly number[]; speed: number };
  /** État du joueur conservé d'une zone à l'autre (saut de chasseur, sans passage au dock). */
  carry?: { health: number; shield: number; missiles: number; swarm: number; repairCharges: number };
}

/** Docks présents dans une zone (défaut : zone de départ). */
export function createDocks(data: GameData, zone = data.zones.home): Dock[] {
  const present = zoneOf(data, zone).zone.docks;
  return [new Dock('station', data.station), new Dock('carrier', data.carrier)].filter((d) => present.includes(d.id));
}

export function createFortress(data: GameData, zone = data.zones.home): Fortress | null {
  const fortress = zoneOf(data, zone).zone.fortress;
  return fortress ? new Fortress(fortress) : null;
}

/** Monde de collision d'une zone : rochers + coques des docks + forteresse. Partagé entre sessions par l'application. */
export function createCollisionWorld(data: GameData, zone = data.zones.home): CollisionWorld {
  const boxes = createDocks(data, zone).flatMap((d) => d.boxes());
  boxes.push(...(createFortress(data, zone)?.boxes() ?? []));
  const arena = zoneData(data, zone).arena;
  boxes.push(...stationBoxes(data, arena));
  const ground = zoneOf(data, zone).zone.ground;
  return new CollisionWorld(arena.obstacles, data.rocks.variants, boxes, ground ? groundField(ground) : null);
}

export class Simulation {
  readonly state: SimState;
  readonly mission: Mission;
  readonly docks: Dock[];
  readonly zone: string;
  readonly fortress: Fortress | null;
  readonly brains = new Map<number, EnemyBrain>();
  readonly pilot: PlayerPilot;
  /** Réglages sans équipement, base des changements d'appareil à quai. */
  private baseData: GameData;
  readonly inputLog: PlayerInput[] | null;
  private readonly insideSectors = new Set<SectorId>();
  private dockCooldown = 0;
  private boostWasExhausted = false;
  /** Chargement effectif (objets validés), ou null pour le châssis nu. */
  loadout: Loadout | null;

  /** Charge Rapier puis construit une simulation. */
  static async create(data: GameData, options: SimOptions, collision?: CollisionWorld): Promise<Simulation> {
    await initPhysics();
    return new Simulation(data, options, collision ?? createCollisionWorld(data));
  }

  constructor(data: GameData, readonly options: SimOptions, readonly collision: CollisionWorld) {
    this.zone = zoneOf(data, options.zone ?? data.zones.home).id;
    this.docks = createDocks(data, this.zone);
    this.fortress = createFortress(data, this.zone);
    data = zoneData(data, this.zone);
    this.baseData = data;
    const equipped = options.loadout ? applyLoadout(data, options.shipId, options.loadout) : null;
    this.loadout = equipped?.loadout ?? null;
    if (equipped) data = equipped.data;
    this.state = new SimState(data, collision, options.seed, TICK_DT);
    if (equipped) this.state.equipment = equipped.equipment;
    this.state.docks = this.docks;
    this.state.zone = this.zone;
    this.state.fortress = this.fortress;
    this.state.zoneData = zoneOf(data, this.zone).zone;
    if (options.startTick) {
      this.state.tickCount = options.startTick;
      this.state.time = options.startTick * this.state.dt;
    }
    const profile = data.ships.ships[options.shipId];
    const pose = options.startPose;
    const docked = options.startDocked !== false && !pose;
    // Dock absent de la zone (station hors de la Frontière) : départ au porteur.
    const home = this.docks.find((d) => d.id === (options.startDock ?? 'carrier')) ?? this.dockById('carrier');
    const player = new Ship(
      this.state.newId(),
      'player',
      'player',
      docked ? home.padWorld() : pose ? Vec3.from(pose.position) : new Vec3(0, 0, 0),
      docked ? home.rotation : pose ? Quat.from(pose.rotation) : Quat.identity(),
      profile.health,
      profile.collisionRadius,
      playerMotorProfile(data.ships.flight, profile),
    );
    player.equipShield(this.state.equipment.shield);
    this.state.player = player;
    this.state.ships.push(player);
    this.state.weapons = createPlayerWeapons(options.shipId, profile);
    this.state.control = {
      mode: docked ? 'docked' : 'flight',
      elapsed: 0,
      fromPosition: player.position.clone(),
      fromRotation: player.rotation.clone(),
      autoLaunchAt: docked && options.autoLaunch !== false ? 0.8 : Infinity,
      forwardReleased: false,
      launchRequested: false,
      dockId: home.id,
      missileTimer: 0,
      swarmCarry: 0,
      // Hors mission : réapparitions illimitées (le début de mission fixe les réserves).
      reserves: Infinity,
      deaths: 0,
      docks: 0,
      serviceSeconds: 0,
      repairCharges: this.state.equipment.emergencyRepair?.charges ?? 0,
      jumpCharge: -1,
      jumpTarget: null,
      jumpWasDown: false,
      jumpSelect: null,
      jumpCycleWasDown: false,
    };
    if (docked) player.invulnerableUntil = Infinity;
    this.pilot = new PlayerPilot(options.mouseMode, profile.yawScale, profile.pitchScale, data.ships.flight.mouseJoystick, data.ships.flight.throttle);
    if (pose) {
      player.velocity.copy(player.forward().scale(pose.speed));
      this.pilot.setThrottle(Math.min(1, pose.speed / profile.cruiseMaxSpeed));
    }
    const carry = options.carry;
    if (carry) {
      player.health = Math.max(1, Math.min(player.maxHealth, carry.health));
      player.shield = Math.min(player.maxShield, carry.shield);
      this.state.weapons.missiles = Math.min(this.state.weapons.maxMissiles, carry.missiles);
      this.state.weapons.swarm = Math.min(this.state.weapons.maxSwarm, carry.swarm);
      this.state.control.repairCharges = Math.min(this.state.control.repairCharges, carry.repairCharges);
    }
    this.inputLog = options.recordInputs ? [] : null;
    this.mission = new Mission(this.state, (request) => this.spawn(request));
    // Scénario imposé au départ (tests, pilote automatique) : il démarre tout de suite.
    if (options.mode !== 'sandbox' && this.mission.prepare(options.mode)) this.mission.start();
    this.state.onKill((ship, info) => {
      onScoreKill(this.state, ship, info);
      if (ship === this.state.player) this.onPlayerDown();
    });
  }

  get time(): number { return this.state.time; }
  get player(): Ship { return this.state.player; }
  get control(): PlayerControl { return this.state.control; }
  get carrier(): Dock { return this.dockById('carrier'); }
  get station(): Dock { return this.dockById('station'); }
  /** Dock courant : celui où l'on est amarré, ou le dernier quitté (réapparition). */
  get dock(): Dock { return this.dockById(this.state.control.dockId); }

  dockById(id: DockId): Dock {
    return this.docks.find((d) => d.id === id)!;
  }

  // --- Commandes de dock (menus en jeu) -------------------------------------------

  /** Choisit une mission ; elle démarre au décollage (tout de suite si l'on vole déjà). Faux si une mission est en cours. */
  selectMission(id: string): boolean {
    if (!this.mission.prepare(id)) return false;
    if (this.state.control.mode === 'flight') this.mission.start();
    return true;
  }

  abandonMission(): void {
    this.mission.abandon();
  }

  /** Décollage demandé depuis le menu du dock. */
  requestLaunch(): void {
    if (this.state.control.mode === 'docked') this.state.control.launchRequested = true;
  }

  /**
   * Change d'appareil ou d'équipement, à quai seulement. La coque garde sa proportion, les stocks sont bornés
   * au nouveau maximum ; le service du dock complète ensuite.
   */
  refit(shipId: ShipId, loadout: Partial<Loadout>): boolean {
    const state = this.state;
    if (state.control.mode !== 'docked') return false;
    const equipped = applyLoadout(this.baseData, shipId, loadout);
    const profile = equipped.data.ships.ships[shipId];
    const player = state.player;
    const ratio = player.healthRatio();
    state.data = equipped.data;
    state.equipment = equipped.equipment;
    this.loadout = equipped.loadout;
    player.maxHealth = profile.health;
    player.health = Math.max(1, ratio * profile.health);
    player.motor = playerMotorProfile(state.data.ships.flight, profile);
    player.boostEnergy = player.motor.boostCapacity;
    player.equipShield(state.equipment.shield);
    const w = state.weapons;
    if (w.shipId !== shipId) {
      state.weapons = createPlayerWeapons(shipId, profile);
    } else {
      w.maxMissiles = profile.missiles.max;
      w.missiles = Math.min(w.missiles, w.maxMissiles);
      w.maxSwarm = profile.swarm.max;
      w.swarm = Math.min(w.swarm, w.maxSwarm);
    }
    this.pilot.setScales(profile.yawScale, profile.pitchScale);
    state.control.repairCharges = state.equipment.emergencyRepair?.charges ?? 0;
    return true;
  }

  bossBrain(): BossBrain | null {
    const id = this.mission.bossId;
    const brain = id !== null ? this.brains.get(id) : undefined;
    return brain instanceof BossBrain ? brain : null;
  }

  /** Crée un ennemi ou le boss (directeur de mission, tests, scénarios de dev). */
  spawn(request: SpawnRequest): Ship {
    const { ship, brain } = request.role === 'boss'
      ? createBoss(this.state, request.position, request.rotation)
      : request.role === 'turret'
        ? createTurret(this.state, request.position, request.rotation)
        : createEnemy(this.state, request.role, request.position, request.rotation);
    this.state.ships.push(ship);
    this.brains.set(ship.id, brain);
    if (request.target !== undefined) brain.targetShip = this.state.findShip(request.target);
    return ship;
  }

  tick(input: PlayerInput): void {
    const state = this.state;
    const dt = state.dt;
    this.inputLog?.push(structuredClone(input));

    this.updatePlayer(input, dt);
    if (input.skipIntermission) this.mission.skipIntermission();

    for (const [id, brain] of this.brains) {
      const ship = brain.ship;
      if (!ship.alive) {
        this.brains.delete(id);
        continue;
      }
      const command = brain.update(dt);
      ship.command = command;
      applyRotation(ship, command, dt);
      applyThrust(ship, command, dt);
      this.moveShip(ship, state.data.enemies.common.navigationLimit);
      if (ship.lastBlockNormal) brain.onBlocked(ship.lastBlockNormal);
    }
    // Apparitions demandées pendant le tick (drones du boss), hors suivi des vagues.
    for (const entry of state.spawnQueue.splice(0)) this.spawn(entry);

    updateProjectiles(state, dt);
    this.updateEquipment(dt);
    if (this.control.mode === 'flight') updatePickups(state, dt);
    else for (const p of state.pickups) p.life -= dt;
    updateScore(state, dt);
    this.mission.update(dt);
    this.sample(dt);

    for (let i = state.ships.length - 1; i >= 0; i--) {
      const ship = state.ships[i]!;
      if (!ship.alive && ship.team === 'enemy') state.ships.splice(i, 1);
    }
    state.tickCount++;
    state.time = state.tickCount * dt;
    state.telemetry.setSimSeconds(state.time);
  }

  /**
   * Panneau de réglage (dev) : réapplique l'équipement aux réglages de base modifiés, sans relancer la session.
   */
  retune(base: GameData): void {
    this.baseData = base;
    const state = this.state;
    const shipId = state.weapons.shipId;
    if (this.loadout) {
      const equipped = applyLoadout(base, shipId, this.loadout);
      state.data = equipped.data;
      state.equipment = equipped.equipment;
    } else {
      state.data = base;
    }
    const player = state.player;
    player.motor = playerMotorProfile(state.data.ships.flight, state.data.ships.ships[shipId]);
    player.shieldSpec = state.equipment.shield;
    player.shield = Math.min(player.shield, player.maxShield);
  }

  /** Bouclier (régénération) et réparation d'urgence, après les dégâts du tick. */
  private updateEquipment(dt: number): void {
    const state = this.state;
    const player = state.player;
    if (!player.alive) return;
    player.regenShield(state.time, dt);
    const repair = state.equipment.emergencyRepair;
    const control = state.control;
    if (repair && control.repairCharges > 0 && player.health < player.maxHealth * repair.threshold) {
      const amount = Math.min(repair.amount, player.maxHealth - player.health);
      player.health += amount;
      control.repairCharges--;
      state.emit({ type: 'emergencyRepair', time: state.time, amount, chargesLeft: control.repairCharges });
    }
  }

  // --- Joueur : vol, porteur, réapparition ------------------------------------

  private updatePlayer(input: PlayerInput, dt: number): void {
    const state = this.state;
    const player = state.player;
    const control = state.control;
    const carrier = this.dock;
    const cfg = carrier.data;
    control.elapsed += dt;
    this.dockCooldown = Math.max(0, this.dockCooldown - dt);

    switch (control.mode) {
      case 'flight': {
        const command = this.pilot.command(player, input, dt);
        player.command = command;
        applyRotation(player, command, dt);
        applyThrust(player, command, dt);
        this.moveShip(player, state.data.arena.playerLimit);
        if (player.boostExhausted && !this.boostWasExhausted) state.emit({ type: 'boostDepleted', time: state.time });
        this.boostWasExhausted = player.boostExhausted;
        updateLock(state, dt);
        updatePlayerWeapons(state, input, dt);
        this.updateJumpDrive(input, dt);
        if (this.dockCooldown <= 0) {
          for (const dock of this.docks) {
            const ready = dock.readiness(player);
            if (ready.inZone && ready.speedOk && ready.alignOk) {
              this.beginDocking(dock);
              break;
            }
          }
        }
        break;
      }
      case 'docking': {
        const t = control.elapsed / cfg.dockDuration;
        this.scriptedMove(dockingPose(carrier, control.fromPosition, control.fromRotation, t), dt);
        this.holdWeapons(input);
        if (t >= 1) {
          this.setMode('docked');
          control.autoLaunchAt = Infinity;
          control.docks++;
          state.emit({ type: 'dock', time: state.time, dock: carrier.id, phase: 'docked' });
        }
        break;
      }
      case 'docked': {
        this.scriptedMove({ position: carrier.padWorld(), rotation: carrier.rotation }, dt);
        this.holdWeapons(input);
        this.service(dt);
        const forwardInput = Math.max(input.thrust.z, input.throttleAxis);
        if (forwardInput <= 0.1) control.forwardReleased = true;
        const wantsLaunch = (forwardInput > 0.5 && control.forwardReleased) || input.boost || control.launchRequested;
        if (control.elapsed >= cfg.launchMinDocked && (wantsLaunch || control.elapsed >= control.autoLaunchAt)) {
          control.launchRequested = false;
          this.setMode('launching');
          state.emit({ type: 'dock', time: state.time, dock: carrier.id, phase: 'launching' });
        }
        break;
      }
      case 'launching': {
        const t = control.elapsed / cfg.launchDuration;
        this.scriptedMove({ position: launchPose(carrier, t), rotation: carrier.rotation }, dt);
        this.holdWeapons(input);
        if (t >= 1) {
          this.setMode('flight');
          this.pilot.resetStick();
          // Sortie de catapulte : l'accélérateur reprend la vitesse de lancement.
          this.pilot.setThrottle(cfg.launchSpeed / Math.max(player.motor.cruiseMaxSpeed, 1));
          player.angularVelocity.set(0, 0, 0);
          player.turnState.set(0, 0, 0);
          player.velocity.copy(carrier.forward().scale(cfg.launchSpeed));
          player.invulnerableUntil = state.time + cfg.launchInvulnerability;
          this.dockCooldown = 3;
          state.emit({ type: 'dock', time: state.time, dock: carrier.id, phase: 'launched' });
          // Une mission choisie à quai commence au décollage.
          if (this.mission.phase === 'briefed') this.mission.start();
        }
        break;
      }
      case 'destroyed': {
        this.holdWeapons(input);
        if (control.elapsed >= state.data.carrier.respawn.delay) {
          // Plus de réserve : la mission est perdue, mais le monde continue (réapparition au dock).
          if (this.mission.active && control.reserves <= 0) this.mission.fail();
          this.respawn();
        }
        break;
      }
    }
  }

  /** Zones que le module de saut peut atteindre d'ici (espace vers espace). */
  jumpTargets(): string[] {
    return jumpTargets(this.state.data, this.zone, 'fighter');
  }

  /** Destination du module de saut : celle choisie (touche G) si elle reste possible, sinon la première. */
  jumpDestination(): string | null {
    const targets = this.jumpTargets();
    const chosen = this.state.control.jumpSelect;
    return chosen && targets.includes(chosen) ? chosen : targets[0] ?? null;
  }

  /**
   * Module de saut (chasseurs lourds) : un appui lance la charge vers la zone suivante, un second l'annule.
   * Hors mission seulement. Charge terminée : événement `ready`, le saut lui-même est exécuté par l'application
   * (animation, nouvelle simulation dans la zone).
   */
  private updateJumpDrive(input: PlayerInput, dt: number): void {
    const state = this.state;
    const control = state.control;
    const pressed = input.jump && !control.jumpWasDown;
    control.jumpWasDown = input.jump;
    const cycle = input.jumpCycle && !control.jumpCycleWasDown;
    control.jumpCycleWasDown = input.jumpCycle;
    const drive = state.equipment.jumpDrive;
    if (cycle && control.jumpCharge < 0 && drive) {
      const targets = this.jumpTargets();
      if (targets.length > 0) {
        const current = this.jumpDestination();
        control.jumpSelect = targets[(targets.indexOf(current ?? '') + 1) % targets.length]!;
        state.emit({ type: 'jumpDrive', time: state.time, phase: 'selected', target: control.jumpSelect, duration: drive.charge });
      }
    }
    if (control.jumpCharge >= 0) {
      if (pressed || this.mission.active) {
        this.cancelJump();
        return;
      }
      control.jumpCharge += dt;
      if (drive && control.jumpCharge >= drive.charge) {
        state.emit({ type: 'jumpDrive', time: state.time, phase: 'ready', target: control.jumpTarget, duration: drive.charge });
        control.jumpCharge = -1;
      }
      return;
    }
    if (!pressed) return;
    if (!drive) {
      state.emit({ type: 'jumpDrive', time: state.time, phase: 'blocked', target: null, duration: 0, reason: 'noDrive' });
    } else if (this.mission.active || this.mission.phase === 'briefed') {
      state.emit({ type: 'jumpDrive', time: state.time, phase: 'blocked', target: null, duration: 0, reason: 'mission' });
    } else if (!this.jumpDestination()) {
      state.emit({ type: 'jumpDrive', time: state.time, phase: 'blocked', target: null, duration: 0, reason: 'noTarget' });
    } else {
      control.jumpCharge = 0;
      control.jumpTarget = this.jumpDestination();
      state.emit({ type: 'jumpDrive', time: state.time, phase: 'charging', target: control.jumpTarget, duration: drive.charge });
    }
  }

  private cancelJump(): void {
    const control = this.state.control;
    if (control.jumpCharge < 0) return;
    control.jumpCharge = -1;
    this.state.emit({ type: 'jumpDrive', time: this.state.time, phase: 'cancelled', target: control.jumpTarget, duration: 0 });
  }

  private setMode(mode: PlayerControl['mode']): void {
    const control = this.state.control;
    // Charge de saut interrompue dès que l'on quitte le vol (appontage, destruction).
    if (mode !== 'flight') this.cancelJump();
    control.mode = mode;
    control.elapsed = 0;
    control.fromPosition = this.state.player.position.clone();
    control.fromRotation = this.state.player.rotation.clone();
  }

  private beginDocking(dock: Dock): void {
    const state = this.state;
    state.control.dockId = dock.id;
    this.setMode('docking');
    this.pilot.setThrottle(0);
    state.control.forwardReleased = false;
    state.player.invulnerableUntil = Infinity;
    resetLock(state);
    state.emit({ type: 'dock', time: state.time, dock: dock.id, phase: 'docking' });
  }

  /** Séquence scriptée : pose imposée, vitesse déduite pour la caméra et le son. */
  private scriptedMove(pose: { position: Vec3; rotation: Quat }, dt: number): void {
    const player = this.state.player;
    const velocity = pose.position.sub(player.position).scale(1 / dt);
    player.position.copy(pose.position);
    player.rotation = pose.rotation.clone();
    player.velocity.copy(velocity);
    player.command = emptyCommand();
    const f = player.flight;
    f.boosting = false;
    f.braking = false;
    f.reversing = false;
    f.envelope = 'cruise';
    f.forwardInput = 0;
    f.signedForwardSpeed = velocity.dot(player.forward());
    f.boostElapsed = -1;
  }

  /** Hors vol : pas de verrou ni de tir, et les fronts de boutons restent cohérents au retour en vol. */
  private holdWeapons(input: PlayerInput): void {
    const w = this.state.weapons;
    if (w.lockState !== 'none') resetLock(this.state);
    w.primaryWasDown = input.firePrimary;
    w.secondaryWasDown = input.fireSecondary;
  }

  /** Réparation et ravitaillement à quai. */
  private service(dt: number): void {
    const state = this.state;
    const s = this.dock.data.service;
    const player = state.player;
    const w = state.weapons;
    const control = state.control;
    let busy = false;
    if (player.health < player.maxHealth) {
      player.health = Math.min(player.maxHealth, player.health + s.repairRate * dt);
      busy = true;
    }
    if (w.missiles < w.maxMissiles) {
      control.missileTimer += dt;
      while (control.missileTimer >= s.missileInterval && w.missiles < w.maxMissiles) {
        control.missileTimer -= s.missileInterval;
        w.missiles++;
      }
      busy = true;
    }
    if (w.swarm < w.maxSwarm) {
      control.swarmCarry += s.swarmRate * dt;
      const whole = Math.floor(control.swarmCarry);
      w.swarm = Math.min(w.maxSwarm, w.swarm + whole);
      control.swarmCarry -= whole;
      busy = true;
    }
    w.heat = 0;
    w.overheated = false;
    player.boostEnergy = player.motor.boostCapacity;
    player.boostExhausted = false;
    player.shield = player.maxShield;
    control.repairCharges = state.equipment.emergencyRepair?.charges ?? 0;
    if (busy) {
      control.serviceSeconds += dt;
      state.telemetry.recordService(dt);
    }
  }

  private onPlayerDown(): void {
    const state = this.state;
    const control = state.control;
    control.deaths++;
    this.setMode('destroyed');
    resetLock(state);
    resetStreak(state);
    state.emit({ type: 'playerDown', time: state.time, reserves: Number.isFinite(control.reserves) ? control.reserves : -1 });
  }

  /** Réapparition au dernier dock, coque pleine, stocks au moins au niveau initial. */
  private respawn(): void {
    const state = this.state;
    const control = state.control;
    const player = state.player;
    const profile = state.data.ships.ships[state.weapons.shipId];
    const w = state.weapons;
    control.reserves -= 1;
    player.alive = true;
    player.health = player.maxHealth;
    player.position.copy(this.dock.padWorld());
    player.rotation = this.dock.rotation.clone();
    player.velocity.set(0, 0, 0);
    player.boostEnergy = player.motor.boostCapacity;
    player.boostExhausted = false;
    player.equipShield(state.equipment.shield);
    control.repairCharges = state.equipment.emergencyRepair?.charges ?? 0;
    player.invulnerableUntil = Infinity;
    w.missiles = Math.max(w.missiles, profile.missiles.initial);
    w.swarm = Math.max(w.swarm, profile.swarm.initial);
    w.heat = 0;
    w.overheated = false;
    this.setMode('docked');
    control.autoLaunchAt = this.options.autoLaunch !== false ? state.data.carrier.respawn.autoLaunchDelay : Infinity;
    control.forwardReleased = false;
    control.launchRequested = false;
    state.emit({ type: 'respawn', time: state.time, reserves: Number.isFinite(control.reserves) ? control.reserves : -1 });
  }

  /** Déplacement avec glissement sur les obstacles, puis limite de l'arène (vitesse sortante annulée). */
  private moveShip(ship: Ship, limit: number): void {
    const result = this.collision.slideMove(ship.position, ship.velocity, ship.radius, this.state.dt);
    ship.position.copy(result.position);
    ship.velocity.copy(result.velocity);
    ship.lastBlockNormal = result.blocked;
    const p = ship.position, v = ship.velocity;
    if (Math.abs(p.x) > limit) { if (Math.sign(v.x) === Math.sign(p.x)) v.x = 0; p.x = Math.sign(p.x) * limit; }
    if (Math.abs(p.y) > limit) { if (Math.sign(v.y) === Math.sign(p.y)) v.y = 0; p.y = Math.sign(p.y) * limit; }
    if (Math.abs(p.z) > limit) { if (Math.sign(v.z) === Math.sign(p.z)) v.z = 0; p.z = Math.sign(p.z) * limit; }
  }

  private sample(dt: number): void {
    const state = this.state;
    const player = state.player;
    if (!player.alive || this.mission.finished || this.control.mode !== 'flight') return;
    state.telemetry.sampleFlight(dt, player.velocity.length(), player.flight.signedForwardSpeed, player.flight.boosting, player.flight.braking);
    let heavyClose = false, rearPressure = false, marksmanFar = false;
    const forward = player.forward();
    for (const ship of state.ships) {
      if (!ship.alive || ship.team !== 'enemy') continue;
      const to = ship.position.sub(player.position);
      const d = to.length();
      if (ship.kind === 'heavy') {
        if (d < 60) heavyClose = true;
        else if (d <= 150 && forward.dot(to.normalized()) < -0.35) rearPressure = true;
      } else if (ship.kind === 'marksman' && d >= 140 && d <= 250) {
        marksmanFar = true;
      }
    }
    state.telemetry.sampleSpatial(dt, heavyClose, rearPressure, marksmanFar);
    for (const sector of state.data.arena.sectors) {
      const inside = player.position.distanceTo(Vec3.from(sector.center)) <= sector.visitRadius;
      if (inside && !this.insideSectors.has(sector.id)) {
        this.insideSectors.add(sector.id);
        state.emit({ type: 'sectorEntered', time: state.time, sector: sector.id });
      } else if (!inside) {
        this.insideSectors.delete(sector.id);
      }
    }
  }
}

export type { PlayerInput, MouseMode, MissionMode };
